import React, { useState, useRef } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from './SortableBookmarkItem';
import { ExportBookmarkItem } from './ExportBookmarkItem';
import { useBookmark } from '../contexts/BookmarkContext';
import { useLanguage } from '../contexts/LanguageContext';
import html2canvas from 'html2canvas';
import { logger } from '../utils/logger';
import { analytics } from '../utils/analytics';

interface ItineraryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isEmbedded?: boolean;
  dragAttributes?: any;
  dragListeners?: any;
  isDragging?: boolean;
  onResetPosition?: () => void;
  onDragStart?: () => void;
}

export const ItineraryPanel: React.FC<ItineraryPanelProps> = ({ 
  isOpen, 
  onClose, 
  isEmbedded = false, 
  dragAttributes, 
  dragListeners, 
  isDragging: externalIsDragging, 
  onResetPosition, 
  onDragStart 
}) => {
  const { bookmarks, removeBookmark, reorderBookmarks } = useBookmark();
  const { language, t } = useLanguage();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = bookmarks.findIndex(bookmark => bookmark.id === active.id);
      const newIndex = bookmarks.findIndex(bookmark => bookmark.id === over.id);
      
      const newOrder = arrayMove(bookmarks, oldIndex, newIndex);
      reorderBookmarks(newOrder);
    }
  };



  const handleExport = async () => {
    if (!exportRef.current || bookmarks.length === 0) return;

    try {
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: false
      } as any);

      // Convert to JPEG blob
      canvas.toBlob((blob) => {
        if (!blob) return;
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ferry-trips-${new Date().toISOString().split('T')[0]}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Track export action
        analytics.bookmarkAction('export', `${bookmarks.length} trips`);
      }, 'image/jpeg', 0.95);
    } catch (error) {
      logger.error('Failed to export image:', error);
      analytics.trackError('export_image_failed', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  if (!isOpen && !isEmbedded) return null;

  if (isEmbedded) {
    return (
      <div className="rounded-lg transition-all duration-300 overflow-hidden w-full" style={{backgroundColor: '#F4F5F7'}}>
        {/* Header */}
        <div 
          {...(dragAttributes || {})}
          {...(dragListeners || {})}
          className={`flex items-center justify-between p-4 border-b min-w-0 group ${
            dragAttributes ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
          style={{ touchAction: 'none' }}
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
        >
          <div className="flex items-center justify-between w-full">
            <h2 className={`text-lg font-semibold text-gray-900 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
              My Trips
            </h2>
            
            {/* Centered Grip Dots */}
            <div className="flex-1 mx-6 flex justify-center items-center">
              <div className={`grid grid-cols-8 gap-1 transition-colors duration-200`}>
                {Array.from({ length: 16 }).map((_, index) => (
                  <div
                    key={index}
                    className={`w-1 h-1 rounded-full transition-colors ${
                      externalIsDragging 
                        ? 'bg-gray-700' 
                        : 'bg-gray-200 group-hover:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
            {!isCollapsed && bookmarks.length > 0 && (
              <button
                onClick={handleExport}
                className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                title="Export as image"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            )}
            {onResetPosition && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onResetPosition();
                }}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded"
                title="Reset position"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:block p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              aria-label={isCollapsed ? "Expand" : "Collapse"}
            >
              <svg 
                className={`w-4 h-4 transition-transform ${isCollapsed ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div 
          className={`overflow-y-auto transition-all duration-300 ${
            isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[70vh] opacity-100'
          }`}
        >
            {bookmarks.length === 0 ? (
              <div className="p-6 text-center">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <p className={`text-gray-500 text-sm ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                  Add your trips here
                </p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={bookmarks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                  <div className="p-4 space-y-3">
                    {bookmarks.map((bookmark) => (
                      <SortableItem
                        key={bookmark.id}
                        id={bookmark.id}
                        bookmark={bookmark}
                        onRemove={() => removeBookmark(bookmark.id)}
                        language={language}
                        t={t}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
        </div>

        {/* Hidden Export Container */}
        <div 
          ref={exportRef} 
          className="fixed -top-[9999px] left-0 bg-white"
          style={{ width: '800px', minHeight: '100px' }}
        >
          <div className="p-8">
            <div className="text-center mb-6">
              <h1 className={`text-2xl font-bold text-gray-900 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                My Ferry Trips
              </h1>
              <p className={`text-gray-600 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                {new Date().toLocaleDateString()}
              </p>
            </div>
            {bookmarks.map((bookmark) => (
              <ExportBookmarkItem
                key={bookmark.id}
                bookmark={bookmark}
                language={language}
                t={t}
              />
            ))}
          </div>
        </div>

      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className={`text-xl font-semibold text-gray-900 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
              {t.myBookmarks}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {bookmarks.length === 0 ? (
              <div className="p-6 text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <h3 className={`text-lg font-medium text-gray-900 mb-2 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                  {t.noBookmarks}
                </h3>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={bookmarks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                  <div className="p-4 space-y-4">
                    {bookmarks.map((bookmark) => (
                      <SortableItem
                        key={bookmark.id}
                        id={bookmark.id}
                        bookmark={bookmark}
                        onRemove={() => removeBookmark(bookmark.id)}
                        language={language}
                        t={t}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Footer Actions */}
          {bookmarks.length > 0 && (
            <div className="border-t p-4">
              <button
                onClick={handleExport}
                className={`w-full px-4 py-2 text-blue-600 hover:text-blue-800 font-medium transition-colors ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}
              >
                Export as Image
              </button>
            </div>
          )}
        </div>

        {/* Hidden Export Container for Modal */}
        <div 
          ref={exportRef} 
          className="fixed -top-[9999px] left-0 bg-white"
          style={{ width: '800px', minHeight: '100px' }}
        >
          <div className="p-8">
            <div className="text-center mb-6">
              <h1 className={`text-2xl font-bold text-gray-900 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                My Ferry Trips
              </h1>
              <p className={`text-gray-600 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                {new Date().toLocaleDateString()}
              </p>
            </div>
            {bookmarks.map((bookmark) => (
              <ExportBookmarkItem
                key={bookmark.id}
                bookmark={bookmark}
                language={language}
                t={t}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};